"use client";

import Button from "@/components/Button";
import { SERVICEABILITY_ADDRESS_NR_MASK_PROPS } from "@/constants/newRelicSessionReplay";
import { useAppContext } from "@/context/AppContext";
import { useServiceability } from "@/hooks/useServiceability";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import fireFieldInputted from "@/lib/tagular/fire-field-inputted";
import type { ServiceabilityAddressSuggestion } from "@/types/serviceability";
import { FormEvent, useEffect } from "react";
import { DEFAULT_LOCALIZATION_BANNER_DATA } from "./constants";
import type { LocalizationBannerData, LocalizationBannerProps } from "./types";

export default function LocalizationBanner({
  title,
  label,
  showWhenLocalized = false,
}: LocalizationBannerProps) {
  const { isLoading, localizationType } = useAppContext();

  if (!showWhenLocalized && (isLoading || localizationType === "full")) {
    return null;
  }

  return <LocalizationBannerForm title={title} label={label} />;
}

function LocalizationBannerForm({ title, label }: LocalizationBannerData) {
  const titleCopy =
    title?.copy?.trim() || DEFAULT_LOCALIZATION_BANNER_DATA.title.copy;
  const labelCopy =
    label?.copy?.trim() || DEFAULT_LOCALIZATION_BANNER_DATA.label?.copy;
  const {
    inputValue,
    suggestions,
    searchStatus,
    submitStatus,
    nextUrl,
    setInputValue,
    selectAddress,
    submitSelection,
    canSubmit,
  } = useServiceability();

  useEffect(() => {
    if (nextUrl) {
      window.location.href = nextUrl;
    }
  }, [nextUrl]);

  const handleAddressSelect = (address: ServiceabilityAddressSuggestion) => {
    selectAddress(address, {
      autofilled: true,
      fieldId: "localization-banner-input",
      fieldLabel: labelCopy,
      fieldName: "serviceabilityAddress",
      fieldType: "dropdown",
      fieldValue: "",
    });
  };

  const trackAddressFieldEngaged = () => {
    fireElementClicked({
      elementType: "textInput",
      location: "hero",
      text: "Address input focused",
      name: "Field EngagedLocalizationBanner",
    });
  };

  const trackAddressInputBlur = () => {
    if (!inputValue.trim()) return;

    fireFieldInputted({
      autofilled: false,
      fieldId: "localization-banner-input",
      fieldLabel: labelCopy,
      fieldName: "serviceabilityAddress",
      fieldType: "text",
      fieldValue: "",
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submitSelection();
  };

  return (
    <section
      className="bg-transparent px-6 py-10 text-neutral-base md:px-16"
      aria-label="Find offers for your address"
    >
      <div className="mx-auto flex max-w-328 flex-col items-center justify-center gap-5">
        <h2 className="title-xl-bold max-w-81.75 text-center md:title-2xl-bold md:max-w-full">
          {titleCopy}
        </h2>

        <form
          className="flex w-full flex-col items-stretch gap-3 md:w-auto md:flex-row md:items-end md:gap-1.5"
          onSubmit={handleSubmit}
        >
          <div className="relative w-full md:w-122.5">
            <label
              className="body-xs-normal mb-1 block text-neutral-base"
              htmlFor="localization-banner-input"
            >
              {labelCopy}
            </label>
            <div className="relative">
              <input
                id="localization-banner-input"
                type="text"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                onFocus={trackAddressFieldEngaged}
                onBlur={trackAddressInputBlur}
                aria-label={labelCopy}
                placeholder="123 Main St, Apt J, Pleasantville, MA 01040"
                autoComplete="street-address"
                className="body-base-normal h-14 w-full rounded border border-neutral-700 bg-white px-4 py-4 text-neutral-base outline-none transition-colors placeholder:text-neutral-600 focus:border-neutral-950"
              />
            </div>

            {searchStatus === "searching" && (
              <p className="body-sm-normal mt-2 text-neutral-base/80">
                Searching addresses...
              </p>
            )}

            {searchStatus === "empty" && (
              <p className="body-sm-normal mt-2 text-neutral-base/80">
                No address matches yet.
              </p>
            )}

            {suggestions.length > 0 && (
              <div
                data-testid="localization-banner-suggestions"
                className="absolute left-0 right-0 top-full z-50 mt-2 max-h-80 overflow-y-auto rounded border border-neutral-300 bg-white shadow-lg"
              >
                {suggestions.map((suggestion) => (
                  <button
                    key={`${suggestion.fullAddress}-${suggestion.locationId ?? suggestion.geofabricId ?? "missing-id"}`}
                    type="button"
                    className="body-sm-normal w-full px-4 py-3 text-left transition-colors hover:bg-neutral-light"
                    onClick={() => handleAddressSelect(suggestion)}
                    {...SERVICEABILITY_ADDRESS_NR_MASK_PROPS}
                  >
                    {suggestion.fullAddress}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={!canSubmit}
            display="fill"
            tone="neutral-dark"
            size="md"
            className="w-40 self-center md:self-auto"
          >
            {submitStatus === "submitting" ? "Finding..." : "Find My Plan"}
          </Button>
        </form>

        <p className="body-xs-normal text-center text-neutral-base">
          Already a member?{" "}
          <a
            href="https://login.xfinity.com/login"
            className="text-theme1-base underline"
          >
            Sign in here.
          </a>
        </p>
      </div>
    </section>
  );
}
